import React from 'react';
import VirtualizedVideoGrid from '../virtualized/VirtualizedVideoGrid';
import VirtualizedImageGrid from '../virtualized/VirtualizedImageGrid';
import { VideoRowSkeleton, ImageRowSkeleton } from '../ui/skeleton/Skeleton';
import { StateDisplay } from '@/components/ui/common/GallerySharedComponents';
import VideoDetailsModal from '@/components/dashboard/gallery/VideoDetailsModal';
import { type UserVideo } from '@/api/videoApi';
import { type UserImage } from '@/api/imageApi';

interface MediaContentConfig {
  icon: React.ReactNode;
  iconBgColor: string;
  title: string;
  description: string;
  skeletonKeyPrefix: string;
}

interface VideoContentProps {
  videos: UserVideo[];
  isLoading: boolean;
  columns: number;
  config: MediaContentConfig;
  onVideoClick: (video: UserVideo) => void;
  getStatusBadge: (status?: string) => React.ReactNode;
  // isAdmin: boolean;
  isFavouritesPage?: boolean;
  isArchivesPage?: boolean;
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  // onArchiveSuccess?: (isArchived: boolean) => void;
}

interface ImageContentProps {
  images: UserImage[];
  isLoading: boolean;
  columns: number;
  config: MediaContentConfig;
  onImageClick: (image: UserImage) => void;
  showUserEmail: boolean;
  isAdmin: boolean;
  isFavouritesPage?: boolean;
  isArchivesPage?: boolean;
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  onArchiveSuccess?: (isArchived: boolean) => void;
}

/**
 * Shared component for rendering video content with loading and empty states
 */
export const VideoContentRenderer: React.FC<VideoContentProps> = ({
  videos,
  isLoading,
  columns,
  config,
  onVideoClick,
  getStatusBadge,
  // isAdmin,
  isFavouritesPage,
  isArchivesPage,
  onFavouriteSuccess,
  // onArchiveSuccess,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }, (_, i) => (
          <VideoRowSkeleton key={`${config.skeletonKeyPrefix}-${i}`} columns={columns} rowIndex={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (videos.length === 0) {
    return (
      <StateDisplay
        icon={config.icon}
        iconBgColor={config.iconBgColor}
        title={config.title}
        description={config.description}
      />
    );
  }

  // Videos grid
  return (
    <VirtualizedVideoGrid
      videos={videos}
      isLoading={isLoading}
      onVideoClick={onVideoClick}
      getStatusBadge={getStatusBadge}
      // isAdmin={isAdmin}
      isFavouritesPage={isFavouritesPage}
      isArchivesPage={isArchivesPage}
      onFavouriteSuccess={onFavouriteSuccess}
      // onArchiveSuccess={onArchiveSuccess}
    />
  );
};

/**
 * Shared component for rendering image content with loading and empty states
 */
export const ImageContentRenderer: React.FC<ImageContentProps> = ({
  images,
  isLoading,
  columns,
  config,
  onImageClick,
  showUserEmail,
  isAdmin,
  isFavouritesPage,
  isArchivesPage,
  onFavouriteSuccess,
  onArchiveSuccess,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }, (_, i) => (
          <ImageRowSkeleton key={`${config.skeletonKeyPrefix}-${i}`} columns={columns} rowIndex={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (images.length === 0) {
    return (
      <StateDisplay
        icon={config.icon}
        iconBgColor={config.iconBgColor}
        title={config.title}
        description={config.description}
      />
    );
  }

  // Images grid
  return (
    <VirtualizedImageGrid
      images={images}
      isLoading={isLoading}
      onImageClick={onImageClick}
      showUserEmail={showUserEmail}
      isAdmin={isAdmin}
      isFavouritesPage={isFavouritesPage}
      isArchivesPage={isArchivesPage}
      onFavouriteSuccess={onFavouriteSuccess}
      onArchiveSuccess={onArchiveSuccess}
    />
  );
};

/**
 * Shared component for rendering the video/image details modal
 */
interface MediaDetailsModalProps {
  selectedVideo: UserVideo | null;
  selectedImage: UserImage | null;
  onClose: () => void;
  pageValue: 'Videos' | 'Images';
  userEmail: string | null;
  isAdmin: boolean;
  getStatusBadge: (status?: string) => React.ReactElement;
  formatDate: (date: string) => string;
  formatFileSize: (bytes: number) => string;
  onRequestDelete: (videoId: string) => Promise<void>;
  onRequestImageDelete: (imageId: string) => Promise<void>;
  onDelete?: (videoId: string) => Promise<void>;
  onImageDelete?: (imageId: string) => Promise<void>;
}

export const MediaDetailsModal: React.FC<MediaDetailsModalProps> = (props) => {
  return <VideoDetailsModal {...props} />;
};

/**
 * Shared component for rendering content grid and media details modal
 */
interface MediaPageContentProps {
  pageValue: 'Videos' | 'Images';
  renderVideosContent: () => React.ReactNode;
  renderImagesContent: () => React.ReactNode;
  selectedVideo: UserVideo | null;
  selectedImage: UserImage | null;
  onClose: () => void;
  userEmail: string | null;
  isAdmin: boolean;
  getStatusBadge: (status?: string) => React.ReactElement;
  formatDate: (date: string) => string;
  formatFileSize: (bytes: number) => string;
  onRequestDelete: (videoId: string) => Promise<void>;
  onRequestImageDelete: (imageId: string) => Promise<void>;
  onDelete?: (videoId: string) => Promise<void>;
  onImageDelete?: (imageId: string) => Promise<void>;
}

export const MediaPageContent: React.FC<MediaPageContentProps> = ({
  pageValue,
  renderVideosContent,
  renderImagesContent,
  selectedVideo,
  selectedImage,
  onClose,
  userEmail,
  isAdmin,
  getStatusBadge,
  formatDate,
  formatFileSize,
  onRequestDelete,
  onRequestImageDelete,
  onDelete,
  onImageDelete,
}) => {
  return (
    <>
      {/* Content Grid */}
      <div className="mx-auto">
        {pageValue === 'Videos' ? renderVideosContent() : renderImagesContent()}
      </div>

      {/* Video/Image Details Modal */}
      <MediaDetailsModal
        selectedVideo={selectedVideo}
        selectedImage={selectedImage}
        onClose={onClose}
        pageValue={pageValue}
        userEmail={userEmail}
        isAdmin={isAdmin}
        getStatusBadge={getStatusBadge}
        formatDate={formatDate}
        formatFileSize={formatFileSize}
        onRequestDelete={onRequestDelete}
        onRequestImageDelete={onRequestImageDelete}
        onDelete={onDelete}
        onImageDelete={onImageDelete}
      />
    </>
  );
};
